import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';

const DEG = 180 / Math.PI, RAD = Math.PI / 180;
const GC = '#4a7c1f';
const lbl = { fill: GC, fontSize: 13, fontStyle: 'italic', fontFamily: 'serif' };
const dashed = { stroke: GC, strokeDasharray: '3 3', fill: 'none' };

/* Parse "pi/3" or "π/4" etc. as radians shortcut */
function parseValue(v, unit) {
  if (v === '' || v == null) return null;
  if (typeof v === 'number') return v;
  const s = String(v).trim().toLowerCase().replace(/π/g, 'pi');
  if (unit === 'rad' && /^-?pi(?:\/-?\d+(?:\.\d+)?)?$/.test(s)) {
    const m = s.match(/^(-?)pi(?:\/(-?\d+(?:\.\d+)?))?$/);
    const sign = m[1] === '-' ? -1 : 1;
    const den = m[2] ? Number(m[2]) : 1;
    return sign * Math.PI / den;
  }
  const num = Number(s);
  return isFinite(num) ? num : null;
}

function RightTriDiagram({ a, b, c, alpha, beta, h }) {
  const A = a || 3, B = b || 4;
  const S = Math.min(240 / A, 260 / B, 45);
  const Ap = [50, 250], Bp = [50 + B * S, 250], Cp = [50, 250 - A * S];
  const dropFoot = [50 + (A * A / (A * A + B * B)) * (Bp[0] - Ap[0]), 250 - (A * B / (A * A + B * B)) * (Cp[1] - 250) - (A * A * A) / ((A * A + B * B))]; // approx
  return (
    <svg viewBox="0 0 340 300" className="w-full max-w-[340px]">
      <polygon points={`${Ap.join(',')} ${Bp.join(',')} ${Cp.join(',')}`} fill="#dbeafe" stroke="#1e3a8a" strokeWidth="2" />
      {/* right-angle marker at A */}
      <rect x={Ap[0]} y={Ap[1] - 14} width="14" height="14" fill="none" stroke="#1e3a8a" strokeWidth="1.5" />
      {/* Altitude from A to hypotenuse — dashed */}
      <line x1={Ap[0]} y1={Ap[1]} x2={dropFoot[0]} y2={dropFoot[1]} {...dashed} />
      <text x={(Ap[0] + dropFoot[0]) / 2 + 4} y={(Ap[1] + dropFoot[1]) / 2 + 4} style={lbl}>h</text>
      {/* Vertex labels */}
      <text x={Cp[0] - 22} y={Cp[1] + 6} fontSize="13" fontWeight="bold" fill="#1e3a8a">β</text>
      <text x={Bp[0] + 6} y={Bp[1] - 4} fontSize="13" fontWeight="bold" fill="#1e3a8a">α</text>
      {/* Side labels */}
      <text x={(Ap[0] + Bp[0]) / 2 - 4} y={Ap[1] + 22} style={lbl}>b</text>
      <text x={Ap[0] - 22} y={(Ap[1] + Cp[1]) / 2 + 4} style={lbl}>a</text>
      <text x={(Bp[0] + Cp[0]) / 2 + 8} y={(Bp[1] + Cp[1]) / 2 - 4} style={lbl}>c</text>
    </svg>
  );
}

/* Solve from any 2 of {a, b, c, α, β, area, perimeter} */
function solveRT(fields, angleUnit) {
  const num = (v) => parseValue(v, 'num');
  const angle = (v) => {
    const n = parseValue(v, angleUnit);
    if (n == null) return null;
    return angleUnit === 'rad' ? n * DEG : n;
  };
  const F = {
    a: num(fields.a), b: num(fields.b), c: num(fields.c),
    alpha: angle(fields.alpha), beta: angle(fields.beta),
    area: num(fields.area), perim: num(fields.perim),
  };
  const known = Object.entries(F).filter(([, v]) => v != null && !isNaN(v) && v > 0);
  if (known.length < 2) return { err: `Please provide any 2 values to compute the rest. (${known.length} given)` };

  let { a, b, c, alpha, beta, area, perim } = F;
  // Convert alpha/beta to canonical: α opposite side a, β opposite side b, γ=90°
  // Given 2 knowns, try to derive a, b, c
  for (let iter = 0; iter < 5; iter++) {
    // From α ↔ β
    if (alpha != null && beta == null) beta = 90 - alpha;
    if (beta != null && alpha == null) alpha = 90 - beta;
    // Pythagoras
    if (a && b && !c) c = Math.sqrt(a * a + b * b);
    if (a && c && !b) b = c > a ? Math.sqrt(c * c - a * a) : null;
    if (b && c && !a) a = c > b ? Math.sqrt(c * c - b * b) : null;
    // Trig
    if (a && alpha && !b) b = a / Math.tan(alpha * RAD);
    if (a && alpha && !c) c = a / Math.sin(alpha * RAD);
    if (b && beta && !a) a = b / Math.tan(beta * RAD);
    if (b && beta && !c) c = b / Math.sin(beta * RAD);
    if (c && alpha && !a) a = c * Math.sin(alpha * RAD);
    if (c && alpha && !b) b = c * Math.cos(alpha * RAD);
    if (c && beta && !b) b = c * Math.sin(beta * RAD);
    if (c && beta && !a) a = c * Math.cos(beta * RAD);
    // Area
    if (area && a && !b) b = 2 * area / a;
    if (area && b && !a) a = 2 * area / b;
    // Perimeter — try if we have two of {a, b, c}
    if (perim && a && b && !c) c = perim - a - b;
    if (perim && a && c && !b) b = perim - a - c;
    if (perim && b && c && !a) a = perim - b - c;
  }
  if (!a || !b) {
    // c + area
    if (c && area && !a) { const twoArea = 2 * area; const disc = c ** 4 - 4 * twoArea * twoArea; if (disc >= 0) { const s = Math.sqrt(disc); a = Math.sqrt((c * c - s) / 2); b = Math.sqrt((c * c + s) / 2); } }
    // alpha + area
    if (alpha && area && !a) { a = Math.sqrt(2 * area * Math.tan(alpha * RAD)); b = 2 * area / a; c = a / Math.sin(alpha * RAD); }
    if (beta && area && !b) { b = Math.sqrt(2 * area * Math.tan(beta * RAD)); a = 2 * area / b; c = b / Math.sin(beta * RAD); }
    // alpha + perim
    if (alpha && perim && !a) { const s = Math.sin(alpha * RAD), t = Math.cos(alpha * RAD); c = perim / (s + t + 1); a = c * s; b = c * t; }
    if (beta && perim && !a) { const s = Math.sin(beta * RAD), t = Math.cos(beta * RAD); c = perim / (s + t + 1); b = c * s; a = c * t; }
  }
  if (!a || !b) {
    if (!c) return { err: 'Insufficient / inconsistent inputs — try another 2-field combination.' };
    // last resort — approximate
  }
  if (!c) c = Math.sqrt(a * a + b * b);
  if (!alpha) alpha = Math.atan(a / b) * DEG;
  if (!beta) beta = 90 - alpha;
  const areaOut = 0.5 * a * b;
  const perimOut = a + b + c;
  const h = (a * b) / c; // altitude from right angle to hypotenuse
  return { a, b, c, alpha, beta, area: areaOut, perim: perimOut, h };
}

export default function RightTriangleCalc() {
  const [a, setA] = useState('3');
  const [b, setB] = useState('');
  const [c, setC] = useState('5');
  const [alpha, setAlpha] = useState('');
  const [beta, setBeta] = useState('');
  const [h, setH] = useState('');
  const [area, setArea] = useState('');
  const [perim, setPerim] = useState('');
  const [angleUnit, setAngleUnit] = useState('deg');
  const [showWork, setShowWork] = useState(false);

  const res = useMemo(() => solveRT({ a, b, c, alpha, beta, area, perim }, angleUnit), [a, b, c, alpha, beta, area, perim, angleUnit]);
  const fmtAngle = (deg) => angleUnit === 'rad' ? (deg * RAD).toFixed(4) + ' rad' : deg.toFixed(3) + '°';

  return (
    <CalcShell look="cnet" title="Right Triangle Calculator" subtitle="Please provide 2 values below to calculate the other values of a right triangle. If radians are selected as the angle unit, it can take values such as pi/3, pi/4, etc.">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4 items-start">
        <div className="cnet-panel">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="flex items-center gap-2"><span className="w-14 font-mono">a =</span><input value={a} onChange={e => setA(e.target.value)} data-testid="rt-a" className="cnet-input flex-1" placeholder="leg a" /></label>
            <label className="flex items-center gap-2"><span className="w-14 font-mono">∠α =</span><input value={alpha} onChange={e => setAlpha(e.target.value)} data-testid="rt-alpha" className="cnet-input flex-1" placeholder="opposite a" />
              <select value={angleUnit} onChange={e => setAngleUnit(e.target.value)} className="cnet-select w-24" data-testid="rt-angle-unit"><option value="deg">degree</option><option value="rad">radian</option></select>
            </label>
            <label className="flex items-center gap-2"><span className="w-14 font-mono">b =</span><input value={b} onChange={e => setB(e.target.value)} data-testid="rt-b" className="cnet-input flex-1" placeholder="leg b" /></label>
            <label className="flex items-center gap-2"><span className="w-14 font-mono">∠β =</span><input value={beta} onChange={e => setBeta(e.target.value)} data-testid="rt-beta" className="cnet-input flex-1" placeholder="opposite b" /><span className="w-24 text-xs text-slate-500 pl-1">{angleUnit}</span></label>
            <label className="flex items-center gap-2"><span className="w-14 font-mono">c =</span><input value={c} onChange={e => setC(e.target.value)} data-testid="rt-c" className="cnet-input flex-1" placeholder="hypotenuse" /></label>
            <label className="flex items-center gap-2"><span className="w-14 font-mono">h =</span><input value={h} onChange={e => setH(e.target.value)} data-testid="rt-h" className="cnet-input flex-1" placeholder="altitude" /></label>
            <label className="flex items-center gap-2"><span className="w-14 font-mono">A =</span><input value={area} onChange={e => setArea(e.target.value)} data-testid="rt-area" className="cnet-input flex-1" placeholder="area" /></label>
            <label className="flex items-center gap-2"><span className="w-14 font-mono">P =</span><input value={perim} onChange={e => setPerim(e.target.value)} data-testid="rt-perim" className="cnet-input flex-1" placeholder="perimeter" /></label>
          </div>
        </div>
        <div className="flex items-center justify-center p-3">
          <RightTriDiagram a={res?.a} b={res?.b} />
        </div>
      </div>

      {res?.err && <div className="mt-4 text-red-600 text-sm bg-red-50 border border-red-200 rounded p-3" data-testid="rt-err">{res.err}</div>}
      {res && !res.err && (
        <>
          <div className="mt-4">
            <div className="bg-[#4c7b25] text-white px-4 py-1.5 text-lg font-bold rounded-t">Result</div>
            <div className="bg-white border border-slate-200 rounded-b p-4" data-testid="rt-result-table">
              <table className="w-full text-sm border border-slate-200">
                <tbody>
                  <tr><td className="px-3 py-2">Leg a</td><td className="px-3 py-2 text-right font-mono">{res.a.toFixed(4)}</td></tr>
                  <tr className="bg-slate-50"><td className="px-3 py-2">Leg b</td><td className="px-3 py-2 text-right font-mono">{res.b.toFixed(4)}</td></tr>
                  <tr><td className="px-3 py-2">Hypotenuse c</td><td className="px-3 py-2 text-right font-mono font-bold text-[#4c7b25]">{res.c.toFixed(4)}</td></tr>
                  <tr className="bg-slate-50"><td className="px-3 py-2">Altitude h (from right angle)</td><td className="px-3 py-2 text-right font-mono">{res.h.toFixed(4)}</td></tr>
                  <tr><td className="px-3 py-2">∠α (opposite a)</td><td className="px-3 py-2 text-right font-mono">{fmtAngle(res.alpha)}</td></tr>
                  <tr className="bg-slate-50"><td className="px-3 py-2">∠β (opposite b)</td><td className="px-3 py-2 text-right font-mono">{fmtAngle(res.beta)}</td></tr>
                  <tr><td className="px-3 py-2">∠γ (right angle)</td><td className="px-3 py-2 text-right font-mono">{angleUnit === 'rad' ? (Math.PI / 2).toFixed(4) + ' rad' : '90.000°'}</td></tr>
                  <tr className="bg-slate-50"><td className="px-3 py-2">Area A</td><td className="px-3 py-2 text-right font-mono font-bold text-[#4c7b25]">{res.area.toFixed(4)}</td></tr>
                  <tr><td className="px-3 py-2">Perimeter P</td><td className="px-3 py-2 text-right font-mono">{res.perim.toFixed(4)}</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-6">
            <button onClick={() => setShowWork(v => !v)} data-testid="rt-show-work-toggle"
              className="text-sm px-4 py-1.5 rounded bg-slate-200 hover:bg-slate-300 text-slate-800 font-medium">
              {showWork ? '− Hide working' : '+ Show working'}
            </button>
            {showWork && (
              <div className="mt-4 p-5 bg-blue-50 border border-blue-100 rounded-xl" data-testid="rt-work-panel">
                <h3 className="text-lg font-bold text-slate-900 mb-3">Step-by-step working</h3>
                <ol className="list-decimal ml-5 space-y-2 text-slate-800 text-[15px] leading-7">
                  <li>Pythagorean theorem: <span className="font-mono">a² + b² = c²</span> → <span className="font-mono">{res.a.toFixed(2)}² + {res.b.toFixed(2)}² = {res.c.toFixed(4)}²</span></li>
                  <li>Angle α: <span className="font-mono">α = arctan(a / b) = {fmtAngle(res.alpha)}</span></li>
                  <li>Angle β: <span className="font-mono">β = 90° − α = {fmtAngle(res.beta)}</span></li>
                  <li>Altitude h to hypotenuse: <span className="font-mono">h = (a · b) / c = {res.h.toFixed(4)}</span></li>
                  <li>Area: <span className="font-mono">A = ½ · a · b = {res.area.toFixed(4)}</span></li>
                  <li>Perimeter: <span className="font-mono">P = a + b + c = {res.perim.toFixed(4)}</span></li>
                </ol>
              </div>
            )}
          </div>
        </>
      )}
    </CalcShell>
  );
}
